import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import './NotificationBell.css';

function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('unread'); // 'unread' | 'all'
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      if (filter === 'all') {
        const res = await axios.get('/notifications');
        const list = res.data?.data || res.data || [];
        setNotifications(Array.isArray(list) ? list : []);
      } else {
        const res = await axios.get('/notifications/unread');
        const list = res.data?.data || res.data || [];
        setNotifications(Array.isArray(list) ? list : []);
      }

      // Also get unread count to keep badge in sync
      const unreadRes = await axios.get('/notifications/unread');
      const unreadList = unreadRes.data?.data || unreadRes.data || [];
      setUnreadCount(Array.isArray(unreadList) ? unreadList.length : 0);
    } catch (err) {
      // Non-critical background polling
    }
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 20000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // When opening dropdown or changing filter, refresh immediately
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchNotifications().finally(() => setLoading(false));
    }
  }, [open, filter, fetchNotifications]);

  // Close dropdown on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleMarkRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.put(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true, isRead: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      if (filter === 'unread') {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }
    } catch (err) {
      // ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read: true, isRead: true }))
      );
      setUnreadCount(0);
      if (filter === 'unread') {
        setNotifications([]);
      }
    } catch (err) {
      // ignore
    }
  };

  const handleNotificationClick = async (notif) => {
    // 1. Mark as read
    if (!notif.read && !notif.isRead) {
      handleMarkRead(notif.id);
    }

    // 2. Close dropdown
    setOpen(false);

    // 3. Navigate if relevant reference is available
    if (notif.type?.includes('PROJECT')) {
      if (notif.refId) {
        navigate(`/projects/${notif.refId}`);
      } else {
        navigate('/projects');
      }
    } else if (notif.type?.includes('ENQUIRY') || notif.type?.includes('FOLLOWUP')) {
      if (notif.refId) {
        navigate(`/enquiries/${notif.refId}`);
      } else {
        navigate('/enquiries');
      }
    }
  };

  const formatRelativeTime = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTypeMeta = (type) => {
    switch (type) {
      case 'FOLLOWUP_DUE':
        return { icon: '⏰', label: 'Follow-up Due', badgeClass: 'badge-due' };
      case 'FOLLOWUP_ASSIGNED':
        return { icon: '📋', label: 'Follow-up Assigned', badgeClass: 'badge-assigned' };
      case 'FOLLOWUP_CREATED':
        return { icon: '💬', label: 'Follow-up Added', badgeClass: 'badge-info' };
      case 'PROJECT_ASSIGNED':
        return { icon: '🏗️', label: 'Project Assigned', badgeClass: 'badge-purple' };
      case 'PROJECT_CREATED':
        return { icon: '🏢', label: 'Project Created', badgeClass: 'badge-blue' };
      case 'ENQUIRY_ASSIGNED':
        return { icon: '✉️', label: 'Enquiry Assigned', badgeClass: 'badge-green' };
      default:
        return { icon: '🔔', label: 'Notification', badgeClass: 'badge-default' };
    }
  };

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className={`bell-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Notifications"
        title="Notifications"
      >
        <svg
          className="bell-icon-svg"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>

        {unreadCount > 0 && (
          <span className="bell-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="bell-backdrop" onClick={() => setOpen(false)} />
          <div className="bell-dropdown animate-fade-in">
            {/* Header */}
            <div className="bell-header">
              <div className="bell-header-title">
                <span className="bell-title-text">Notifications</span>
                {unreadCount > 0 && (
                  <span className="bell-header-pill">{unreadCount} new</span>
                )}
              </div>
              <div className="bell-header-actions">
                {unreadCount > 0 && (
                  <button
                    className="bell-mark-all-btn"
                    onClick={handleMarkAllRead}
                    title="Mark all as read"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  type="button"
                  className="bell-close-btn"
                  onClick={() => setOpen(false)}
                  title="Close"
                  aria-label="Close notifications"
                >
                  &times;
                </button>
              </div>
            </div>

          {/* Filter Tabs */}
          <div className="bell-tabs">
            <button
              className={`bell-tab ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread {unreadCount > 0 && `(${unreadCount})`}
            </button>
            <button
              className={`bell-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Notifications
            </button>
          </div>

          {/* List Content */}
          <div className="bell-list-container">
            {loading ? (
              <div className="bell-loading">
                <div className="bell-spinner"></div>
                <span>Loading notifications...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="bell-empty">
                <div className="bell-empty-icon">🔔</div>
                <div className="bell-empty-title">All caught up!</div>
                <p className="bell-empty-desc">
                  {filter === 'unread'
                    ? 'You have no unread notifications.'
                    : 'No notifications to display right now.'}
                </p>
              </div>
            ) : (
              <ul className="bell-list">
                {notifications.map((n) => {
                  const meta = getTypeMeta(n.type);
                  const isUnread = !n.read && !n.isRead;
                  return (
                    <li
                      key={n.id}
                      className={`bell-item ${isUnread ? 'unread' : 'read'}`}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <div className="bell-item-icon">{meta.icon}</div>
                      <div className="bell-item-body">
                        <div className="bell-item-top">
                          <span className={`notif-type-tag ${meta.badgeClass}`}>
                            {meta.label}
                          </span>
                          <span className="notif-time">{formatRelativeTime(n.createdAt)}</span>
                        </div>
                        <p className="notif-message">{n.message}</p>
                      </div>
                      {isUnread && (
                        <button
                          className="bell-unread-dot"
                          onClick={(e) => handleMarkRead(n.id, e)}
                          title="Mark as read"
                          aria-label="Mark as read"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;
